import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import { ManualMimshakData, ManualProductType } from "../../interfaces/DTO/manualMimshakData";
import { Contact } from "../../interfaces/DTO/crm/contact";
import { ReportType, ReportOrigin, MimshakData } from "../../interfaces/DTO/mimshakData";
import CurrencyTextField from "../../shared/UIParts/InputFields/CurrencyTextField";
import ContactAutocomplete from "../../shared/Autocompletes/ContactAutocomplete";
import { formatManualProductType, getFieldConfig, formatManualAdjustedType, generateTempPolicyNumber } from "../../utils/manualProductTypeFormatter";
import { getAllBankHandlerCodes } from "../../utils/bankHandlerCodeFormatter";
import { getAllInvestmentHouseCodesWithBanks } from "../../utils/investmentHouseCodeFormatter";
import SearchAutocomplete from "../../shared/UIParts/SearchAutocomplete";
import { useCreateManualMimshakData } from "../../hooks/useManualMimshaksData";
import { HandlerAutocompleteField } from "../../shared/Autocompletes/HandlerAutocompleteField";
import { buildLoanMimshak } from "../../services/loanMimshakBuilder";
import { buildMortgageMimshak } from "../../services/mortgageMimshakBuilder";
import { buildInvestmentPortfolioMimshak, PortfolioCurrency, PORTFOLIO_CURRENCY_LABEL } from "../../services/investmentPortfolioMimshakBuilder";
import { MiniMortgageLifeInsurance } from "../Mortgage/MiniMortgageLifeInsurance";
import ManualMimshakProductDrawer from "./ManualMimshakProductDrawer";
import SelectNewPathDrawer from "../FeesLab/SelectNewPathDrawer";
import { PathFundSelection } from "../../hooks/feeLab/useNewProductHandlers";
import PercentageTextField from "../../shared/UIParts/InputFields/PercentageTextField";
import { MimshakDataUtil } from "../../services/mimshakDataUtil";
import { PolicyStatus, LiquidityPeriodBalanceType } from "../../interfaces/DTO/mislaka_xml_models";
import { PensionFeeExpenseType } from "../../interfaces/DTO/mislaka_xml_pension_models";

interface ManualAssetFormProps {
  contacts: Contact[];
  productType: ManualProductType;
  onSave?: () => void;
  reportedPeriod?: string;
  initialHandlerCode?: string;
  initialPolicyNumber?: string;
  initialFundsSum?: number;
  mortgageProduct?: MimshakData;
  viewedType?: "USER" | "FAMILY";
}

export const ManualAssetForm: React.FC<ManualAssetFormProps> = ({
  contacts,
  productType,
  onSave,
  reportedPeriod,
  initialHandlerCode,
  initialPolicyNumber,
  initialFundsSum,
  mortgageProduct,
  viewedType,
}) => {
  const createMutation = useCreateManualMimshakData();

  const [savedLoanData, setSavedLoanData] = useState<ManualMimshakData | null>(null);
  const [savedMortgageData, setSavedMortgageData] = useState<ManualMimshakData | null>(null);
  const [openDrawer, setOpenDrawer] = useState(false);

  const [formData, setFormData] = useState<Partial<ManualMimshakData>>({
    contact_id: contacts.length === 1 ? contacts[0]?.id : undefined,
    handler_code: initialHandlerCode || "",
    reported_period: reportedPeriod || new Date().toISOString().slice(0, 7), // YYYY-MM
    product_type: productType,
    adjusted_product_type: productType,
    policy_number: initialPolicyNumber || (getFieldConfig(productType).policyNumberAutoGenerated ? generateTempPolicyNumber() : ""),
    funds_sum: initialFundsSum || 0,
    deposit_amount: 0,
    withdrawal_amount: 0,
    join_date: undefined,
    report_type: ReportType.HOLDINGS_INTERFACE,
    report_origin: ReportOrigin.MANUAL,
    data: {} as any, // אובייקט ריק עבור נתונים ידניים
  });

  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isFamily, setIsFamily] = useState(false);
  const [subTypeString, setSubTypeString] = useState(""); // בחירת סוג-תת (נשמרת ב-adjusted_product_type)
  const [portfolioCurrency, setPortfolioCurrency] = useState<PortfolioCurrency>("ILS");
  const [foreignAmount, setForeignAmount] = useState<number>(0);
  const [pathDrawerOpen, setPathDrawerOpen] = useState(false);
  const [selectedPaths, setSelectedPaths] = useState<PathFundSelection[]>([]);
  const [savingFee, setSavingFee] = useState<number | null>(null);

  const isBankAccount = productType === ManualProductType.BANK_ACCOUNT;
  const isInvestmentPortfolio = productType === ManualProductType.INVESTMENT_PORTFOLIO;
  const isSavingsPolicy = productType === ManualProductType.PURE_SAVINGS_POLICY;
  const isLoan = productType === ManualProductType.LOAN;
  const isMortgage = productType === ManualProductType.MORTGAGE;
  const isDeposit = productType === ManualProductType.DEPOSIT;
  const isBankAccountForeign = productType === ManualProductType.BANK_ACCOUNT_FOREIGN;

  // קבלת קונפיגורציה לפי סוג מוצר
  const fieldConfig = useMemo(() => getFieldConfig(productType), [productType]);

  // רשימות מטפלים
  const bankHandlerCodes = useMemo(() => getAllBankHandlerCodes(), []);
  const investmentHouseCodes = useMemo(() => getAllInvestmentHouseCodesWithBanks(), []);

  // אופציות ל-Autocomplete - בדיקה אם יש אופציות בקונפיגורציה או שימוש בבנקים/בתי השקעות
  const handlerOptions = useMemo(() => {
    // אם יש אופציות מוגדרות בקונפיגורציה, השתמש בהן
    if (fieldConfig.handlerCodeOptions) {
      return fieldConfig.handlerCodeOptions.map(opt => opt.label);
    }
    // אחרת, אם זה בנק, משכנתא או בית השקעות
    const list = (isBankAccount || isBankAccountForeign || isMortgage || isDeposit) ? bankHandlerCodes : isInvestmentPortfolio ? investmentHouseCodes : [];
    return list.map(item => item.label);
  }, [fieldConfig, isBankAccount, isBankAccountForeign, isMortgage, isDeposit, isInvestmentPortfolio, bankHandlerCodes, investmentHouseCodes]);

  // תרגום מ-label ל-value
  const getHandlerValue = (label: string): string => {
    // אם יש אופציות בקונפיגורציה
    if (fieldConfig.handlerCodeOptions) {
      const found = fieldConfig.handlerCodeOptions.find(opt => opt.label === label);
      return found ? found.value : "";
    }
    // אחרת בנקים, משכנתאות או בתי השקעות
    const list = (isBankAccount || isBankAccountForeign || isMortgage || isDeposit) ? bankHandlerCodes : isInvestmentPortfolio ? investmentHouseCodes : [];
    const found = list.find(item => item.label === label);
    return found ? found.value : "";
  };

  // תרגום מ-value ל-label
  const getHandlerLabel = (value: string): string => {
    // אם יש אופציות בקונפיגורציה
    if (fieldConfig.handlerCodeOptions) {
      const found = fieldConfig.handlerCodeOptions.find(opt => opt.value === value);
      return found ? found.label : "";
    }
    // אחרת בנקים, משכנתאות או בתי השקעות
    const list = (isBankAccount || isBankAccountForeign || isMortgage || isDeposit) ? bankHandlerCodes : isInvestmentPortfolio ? investmentHouseCodes : [];
    const found = list.find(item => item.value === value);
    return found ? found.label : (fieldConfig.handlerCodeFreeSolo ? value : "");
  };

  useEffect(() => {
    // אפס את כל השדות בעת החלפת סוג מוצר
    setSelectedPaths([]);
    setSavingFee(null);
    setSubTypeString("");
    setIsFamily(false);
    setFormData(prev => ({
      ...prev,
      product_type: productType,
      adjusted_product_type: productType,
      reported_period: reportedPeriod || new Date().toISOString().slice(0, 7),
      handler_code: initialHandlerCode || "",
      policy_number: initialPolicyNumber || (fieldConfig.policyNumberAutoGenerated ? generateTempPolicyNumber() : ""),
      funds_sum: initialFundsSum !== undefined ? initialFundsSum : 0,
      deposit_amount: 0,
      withdrawal_amount: 0,
      report_type: ReportType.HOLDINGS_INTERFACE,
      report_origin: ReportOrigin.MANUAL,
      data: {} as any,
    }));
  }, [productType, reportedPeriod, initialHandlerCode, initialPolicyNumber, initialFundsSum]);

  const selectedContact = contacts.find((c) => c.id === formData.contact_id) || null;

  const handleSave = async () => {
    try {
      setSaveSuccess(false);

      // הכנת הנתונים לשמירה
      const dataToSave = { ...formData } as ManualMimshakData;

      // אם זו הלוואה, צריך למלא את השדה data עם Mimshak מלא
      if (isLoan) {
        const loanMimshak = buildLoanMimshak(dataToSave);
        dataToSave.data = loanMimshak as any;
      }

      // אם זו משכנתא, צריך למלא את השדה data עם Mimshak מלא
      if (isMortgage) {
        const mortgageMimshak = buildMortgageMimshak(dataToSave);
        dataToSave.data = mortgageMimshak as any;
      }

      // אם זה תיק השקעות, שמור מטבע וסכום זר ב-AccumulatedSavings
      if (isInvestmentPortfolio) {
        const portfolioMimshak = buildInvestmentPortfolioMimshak(dataToSave, portfolioCurrency, foreignAmount);
        dataToSave.data = portfolioMimshak as any;
      }

      // אם זו פוליסת חיסכון טהור — בנה data מלא עם מסלולים ודמי ניהול
      if (isSavingsPolicy) {
        dataToSave.saving_fee = savingFee;
        dataToSave.fund_ids = MimshakDataUtil.buildSavingsPolicyFundIds(selectedPaths);
        dataToSave.data = MimshakDataUtil.buildNewPolicyMimshak({
          handlerCode: dataToSave.handler_code,
          productType,
          policyNumber: dataToSave.policy_number,
          reportedPeriod: dataToSave.reported_period ?? new Date().toISOString().slice(0, 7),
          fundsSum: dataToSave.funds_sum ?? 0,
          selectedPaths,
          savingFee,
        }) as any;
      }

      dataToSave.is_family = isFamily || undefined;

      const savedData = await createMutation.mutateAsync(dataToSave);

      // הצלחה - איפוס הטופס
      setFormData({
        contact_id: contacts.length === 1 ? contacts[0]?.id : undefined,
        handler_code: "",
        reported_period: reportedPeriod || new Date().toISOString().slice(0, 7),
        product_type: productType,
        adjusted_product_type: productType,
        policy_number: "",
        funds_sum: 0,
        deposit_amount: 0,
        withdrawal_amount: 0,
        join_date: undefined,
        report_type: ReportType.HOLDINGS_INTERFACE,
        report_origin: ReportOrigin.MANUAL,
        data: {} as any,
      });

      if (isInvestmentPortfolio) {
        setPortfolioCurrency("ILS");
        setForeignAmount(0);
      }
      if (isSavingsPolicy) {
        setSelectedPaths([]);
        setSavingFee(null);
      }

      setSaveSuccess(true);
      onSave?.();

      // אם זו הלוואה - פתיחת מגירה אוטומטית
      if (isLoan && savedData) {
        setSavedLoanData(savedData);
        setOpenDrawer(true);
      }

      // אם זו משכנתא - פתיחת מגירה אוטומטית
      if (isMortgage && savedData) {
        setSavedMortgageData(savedData);
        setOpenDrawer(true);
      }

      // הסתרת הודעת ההצלחה אחרי 3 שניות
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Failed to save manual mimshak data:", error);
    }
  };

  const handleContactChange = (contact: Contact | null) => {
    if (contact) {
      setFormData({ ...formData, contact_id: contact.id! });
    }
  };

  const hasSubTypeOptions = !!fieldConfig.adjustedTypeOptions;
  const isPolicyAutoGenerated = !!fieldConfig.policyNumberAutoGenerated;
  const isFormValid =
    formData.contact_id &&
    formData.handler_code &&
    formData.policy_number &&
    (!hasSubTypeOptions || !!subTypeString);

  return (
    <Box sx={{ direction: "rtl", maxWidth: "400px" }}>
      <Typography variant="h5" gutterBottom>
        הוספת {formatManualProductType(productType)}
      </Typography>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          הנכס נשמר בהצלחה! הטבלה עודכנה.
        </Alert>
      )}

      {/* הצגת ביטוח משכנתא קיים אם יש */}
      {mortgageProduct && isMortgage && (
        <MiniMortgageLifeInsurance mortgageProduct={mortgageProduct} />
      )}

      <Stack spacing={3} sx={{ mt: 3 }}>
        {/* בחירת לקוח / משפחה */}
        {viewedType === "FAMILY" && fieldConfig.allowFamilySelection && contacts.length > 1 ? (
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>שיוך *</Typography>
            <ToggleButtonGroup
              value={isFamily ? "family" : (formData.contact_id?.toString() ?? "")}
              exclusive
              onChange={(_, val) => {
                if (!val) return;
                if (val === "family") {
                  setIsFamily(true);
                  setFormData({ ...formData, contact_id: contacts[0]?.id });
                } else {
                  setIsFamily(false);
                  setFormData({ ...formData, contact_id: Number(val) });
                }
              }}
              size="small"
              sx={{ flexWrap: "wrap", gap: 0.5 }}
            >
              {contacts.map((c) => (
                <ToggleButton key={c.id} value={c.id!.toString()} sx={{ borderRadius: "8px !important" }}>
                  {c.first_name}
                </ToggleButton>
              ))}
              <ToggleButton value="family" sx={{ borderRadius: "8px !important" }}>
                משפחה
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        ) : contacts.length > 1 && (
          <ContactAutocomplete
            placeholder="בחר לקוח"
            options={contacts}
            value={selectedContact}
            onChange={handleContactChange}
          />
        )}

        {/* תקופת דיווח */}
        <Typography
          variant="caption"
          sx={{ color: "#6d1727", fontWeight: 700, display: "block", textAlign: "left" }}
        >
          תקופת דיווח: {formData.reported_period}
        </Typography>

        {/* קוד מטפל */}
        {isSavingsPolicy ? (
          // עבור פוליסת חיסכון - שימוש ב-HandlerAutocompleteField
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
              {fieldConfig.handlerCodeLabel} *
            </Typography>
            <HandlerAutocompleteField
              value={formData.handler_code}
              onChange={(value) => setFormData({ ...formData, handler_code: value })}
              placeholder={`בחר ${fieldConfig.handlerCodeLabel}...`}
              showIcons={true}
              freeSolo={fieldConfig.handlerCodeFreeSolo}
            />
          </Box>
        ) : fieldConfig.handlerCodeOptions || isBankAccount || isBankAccountForeign || isMortgage || isDeposit || isInvestmentPortfolio ? (
          // עבור סוגי נכסים אחרים עם אופציות מוגדרות
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
              {fieldConfig.handlerCodeLabel} *
            </Typography>
            <SearchAutocomplete
              placeholder={`בחר ${fieldConfig.handlerCodeLabel}...`}
              options={handlerOptions}
              value={getHandlerLabel(formData.handler_code)}
              onChange={(selectedLabel) => {
                if (!selectedLabel) { setFormData({ ...formData, handler_code: "" }); return; }
                const value = getHandlerValue(selectedLabel);
                setFormData({ ...formData, handler_code: value || (fieldConfig.handlerCodeFreeSolo ? selectedLabel : "") });
              }}
              showIcons={!fieldConfig.handlerCodeOptions} // הצג אייקונים רק לבנקים/בתי השקעות
            />
          </Box>
        ) : (
          // שדה טקסט פשוט עבור סוגי נכסים אחרים
          <TextField
            fullWidth
            label={fieldConfig.handlerCodeLabel}
            value={formData.handler_code}
            onChange={(e) =>
              setFormData({ ...formData, handler_code: e.target.value })
            }
            required
          />
        )}

        {/* מספר פוליסה / כתובת / מזהה / סוג מענק */}
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
            {fieldConfig.adjustedTypeOptions ? (fieldConfig.adjustedTypeLabel ?? "סוג") : fieldConfig.policyNumberLabel} *
          </Typography>
          {fieldConfig.adjustedTypeOptions ? (
            <>
              {/* שלב א: בחירת סוג */}
              <SearchAutocomplete
                placeholder={`בחר סוג...`}
                options={fieldConfig.adjustedTypeOptions.map(opt => formatManualAdjustedType(opt.adjustedProductType))}
                value={subTypeString}
                onChange={(selectedLabel) => {
                  const option = fieldConfig.adjustedTypeOptions?.find(opt => formatManualAdjustedType(opt.adjustedProductType) === selectedLabel);
                  setSubTypeString(selectedLabel || "");
                  setFormData({
                    ...formData,
                    adjusted_product_type: option?.adjustedProductType ?? productType,
                  });
                }}
                showIcons={false}
              />
              {/* שלב ב: מספר מזהה — תמיד מוצג אחרי בחירת סוג, disabled אם auto-generated */}
              {subTypeString && (
                <TextField
                  fullWidth
                  label={fieldConfig.policyNumberLabel}
                  value={formData.policy_number}
                  onChange={(e) => { if (!isPolicyAutoGenerated) setFormData({ ...formData, policy_number: e.target.value }); }}
                  disabled={isPolicyAutoGenerated}
                  sx={{ mt: 1.5 }}
                />
              )}
            </>
          ) : (
            <TextField
              fullWidth
              label={fieldConfig.policyNumberLabel}
              value={formData.policy_number}
              onChange={(e) => { if (!isPolicyAutoGenerated) setFormData({ ...formData, policy_number: e.target.value }); }}
              disabled={isPolicyAutoGenerated}
            />
          )}
        </Box>

        {/* יתרה כוללת / שווי נכס */}
        <CurrencyTextField
          label={fieldConfig.fundsSumLabel}
          value={formData.funds_sum || 0}
          onChange={(value) => setFormData({ ...formData, funds_sum: value ?? 0 })}
          fullWidth
        />

        {/* דמי ניהול מצבירה — כל סוג עם savingFeeLabel */}
        {fieldConfig.savingFeeLabel && (
          <PercentageTextField
            label={fieldConfig.savingFeeLabel}
            value={savingFee ?? 0}
            onChange={(val) => setSavingFee(val)}
            decimalPlaces={2}
            max={10}
            sx={{ maxWidth: 200 }}
          />
        )}

        {/* מסלולי השקעה — פוליסת חיסכון טהור */}
        {isSavingsPolicy && (
          <>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                מסלולי השקעה:
              </Typography>
              {selectedPaths.length === 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setPathDrawerOpen(true)}
                  disabled={!formData.handler_code}
                >
                  בחר מסלול...
                </Button>
              )}
              {selectedPaths.length > 0 && (
                <>
                  <Stack spacing={0.5} sx={{ mb: 1 }}>
                    {selectedPaths.map((p, i) => (
                      <Typography key={i} variant="body2" fontWeight="bold">
                        {p.fund.fund_name} ({p.fund.fund_id}) | {p.amount.toLocaleString()} ₪
                      </Typography>
                    ))}
                  </Stack>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setPathDrawerOpen(true)}
                    disabled={!formData.handler_code}
                  >
                    שנה מסלול...
                  </Button>
                </>
              )}
            </Box>
          </>
        )}

        {/* מטבע תיק השקעות + סכום זר */}
        {isInvestmentPortfolio && (
          <>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                מטבע התיק
              </Typography>
              <ToggleButtonGroup
                value={portfolioCurrency}
                exclusive
                onChange={(_, val) => { if (val) { setPortfolioCurrency(val); setForeignAmount(0); } }}
                size="small"
              >
                {(Object.keys(PORTFOLIO_CURRENCY_LABEL) as PortfolioCurrency[]).map((cur) => (
                  <ToggleButton key={cur} value={cur}>
                    {PORTFOLIO_CURRENCY_LABEL[cur]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {portfolioCurrency !== "ILS" && (
              <CurrencyTextField
                label={`שווי תיק ב-${portfolioCurrency === "USD" ? "$" : "€"}`}
                value={foreignAmount}
                onChange={(val) => setForeignAmount(val)}
                currencySymbol={portfolioCurrency === "USD" ? "$" : "€"}
                fullWidth
              />
            )}
          </>
        )}

        {/* סך הפקדות / השקעה ראשונית */}
        {fieldConfig.depositAmountLabel && (
          <CurrencyTextField
            label={fieldConfig.depositAmountLabel}
            value={formData.deposit_amount || 0}
            onChange={(value) => setFormData({ ...formData, deposit_amount: value })}
            fullWidth
          />
        )}

        {/* סך משיכות */}
        {fieldConfig.withdrawalAmountLabel && !isDeposit && (
          <CurrencyTextField
            label={fieldConfig.withdrawalAmountLabel}
            value={formData.withdrawal_amount || 0}
            onChange={(value) =>
              setFormData({ ...formData, withdrawal_amount: value })
            }
            fullWidth
          />
        )}

        {/* תאריך פירעון / הבשלה */}
        {fieldConfig.actualiationDateLabel && (
          <TextField
            fullWidth
            label={fieldConfig.actualiationDateLabel}
            type="date"
            value={formData.actualiation_date || ""}
            onChange={(e) => setFormData({ ...formData, actualiation_date: e.target.value || null })}
            InputLabelProps={{ shrink: true }}
          />
        )}

        {/* תחילת הלוואה - רק להלוואות */}
        {isLoan && (
          <TextField
            fullWidth
            label="תחילת הלוואה"
            type="date"
            value={formData.join_date || ""}
            onChange={(e) =>
              setFormData({ ...formData, join_date: e.target.value })
            }
            InputLabelProps={{
              shrink: true,
            }}
            helperText="תאריך התחלת ההלוואה"
          />
        )}

        {/* כפתור שמירה */}
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="contained"
            startIcon={<Save sx={{ ml: 1 }} />}
            onClick={handleSave}
            disabled={createMutation.isPending || !isFormValid}
            size="large"
          >
            {createMutation.isPending ? "שומר..." : "שמור נכס"}
          </Button>
        </Box>
      </Stack>

      {/* מגירה לעריכת פרטי הלוואה */}
      {savedLoanData && (
        <ManualMimshakProductDrawer
          manualMimshakData={openDrawer ? savedLoanData : null}
          onClose={() => {
            setOpenDrawer(false);
            setSavedLoanData(null);
          }}
          contactName={selectedContact ? `${selectedContact.first_name} ${selectedContact.last_name}` : undefined}
          reportedPeriod={reportedPeriod}
          onSave={onSave}
          initialTab={2} // פתיחה ישירות בטאב מחשבון הלוואה
        />
      )}

      {/* מגירה לעריכת פרטי משכנתא */}
      {savedMortgageData && (
        <ManualMimshakProductDrawer
          manualMimshakData={openDrawer ? savedMortgageData : null}
          onClose={() => {
            setOpenDrawer(false);
            setSavedMortgageData(null);
          }}
          contactName={selectedContact ? `${selectedContact.first_name} ${selectedContact.last_name}` : undefined}
          reportedPeriod={reportedPeriod}
          onSave={onSave}
          initialTab={2} // פתיחה ישירות בטאב מחשבון משכנתא
        />
      )}

      {/* מגירת בחירת מסלול — פוליסת חיסכון טהור */}
      {isSavingsPolicy && pathDrawerOpen && (
        <SelectNewPathDrawer
          open={pathDrawerOpen}
          onClose={() => setPathDrawerOpen(false)}
          mimshak={{
            id: 0,
            policy_number: formData.policy_number || "new",
            handler_code: formData.handler_code || "",
            product_type: productType,
            adjusted_product_type: productType,
            funds_sum: formData.funds_sum ?? 0,
            contact_id: formData.contact_id ?? 0,
            reported_period: formData.reported_period ?? "",
            report_type: ReportType.HOLDINGS_INTERFACE,
            report_origin: ReportOrigin.MANUAL,
            saving_fee: savingFee,
            deposit_fee: 0,
            deposit_amount: 0,
            withdrawal_amount: 0,
            data: {
              products: [{
                details: {
                  handler_code: formData.handler_code || "",
                  product_type: productType,
                  producer_code: formData.handler_code || "",
                  adjusted_product_type: productType,
                },
                policies_or_accounts: [{
                  policy_number: formData.policy_number || "new",
                  status: PolicyStatus.ACTIVE,
                  investment_paths: selectedPaths.map(s => ({
                    path_code: s.fund.fund_id,
                    path_name: s.fund.fund_name ?? "",
                    accumulated_amount: s.amount,
                    management_fee_on_deposit: 0,
                    management_fee_on_savings: savingFee ?? 0,
                  })),
                  program_name: "",
                  update_date: formData.reported_period ?? "",
                  accumulated_savings: [{
                    savings_type_code: LiquidityPeriodBalanceType.CAPITAL,
                    total_savings: formData.funds_sum ?? 0,
                    deposit_type_code: 0,
                    total_redemption_value: 0,
                  }],
                  liquidity_summaries: [],
                }],
              }],
            } as any,
            fund_ids: selectedPaths.map(s => s.fund.fund_id),
          } as MimshakData}
          onFundsSelected={(selections) => {
            setSelectedPaths(selections);
            setPathDrawerOpen(false);
          }}
        />
      )}
    </Box>
  );
};
